/**
 * Concealed-hand decomposition — sibling to `game/WinChecker.ts`'s
 * backtracking, but returns the actual sets found instead of a boolean.
 * Base-point scoring needs to know which concealed tiles formed pungs (they
 * score) vs. runs (they don't) — `WinChecker.isWinningHand` only answers
 * "is this a win", not "how".
 *
 * **Known simplification, not exact tournament-grade scoring:** traditional
 * Chinese Classical scoring takes the MAX over every valid decomposition of
 * a hand (a real combinatorial search). This returns a single deterministic
 * decomposition instead, reusing `canFormMelds`'s exact branch order (pung
 * tried before chi at every step) as a reasonable proxy for the higher-
 * scoring reading in the common case. Fine for a trainer; not a substitute
 * for a real scoring engine. See the plan doc's "Resolved during
 * implementation" section.
 *
 * A few counting helpers are duplicated from `WinChecker.ts` rather than
 * exported from it — `WinChecker.ts` is already near the 200-line cap and
 * these are private implementation details of its backtracking, not a
 * shared contract.
 */

import {
  KIND_COUNT,
  PAIR_SIZE,
  RANKS,
  SET_SIZE,
  SUIT_KIND_COUNT,
  kindIndex,
  type Tile,
  type TileKind,
} from '../game/types.js';

// A run starting at rank r needs r+2 ≤ 9, i.e. an in-suit offset of at most 6.
const MAX_RUN_START_OFFSET = RANKS.length - SET_SIZE;

export type ConcealedSet =
  | { readonly type: 'pung'; readonly kind: TileKind }
  | { readonly type: 'chi' };

export interface ConcealedDecomposition {
  readonly pair: TileKind;
  readonly sets: readonly ConcealedSet[];
}

type KindCounts = number[];

/** Strips `id`, keeping only the identity fields `ConcealedSet.kind` needs. */
function toKind(tile: Tile): TileKind {
  return tile.kind === 'suit'
    ? { kind: 'suit', suit: tile.suit, rank: tile.rank }
    : { kind: 'honor', honor: tile.honor };
}

function countsByKindIndex(tiles: readonly Tile[]): { counts: KindCounts; kindByIndex: Map<number, TileKind> } {
  const counts: KindCounts = new Array<number>(KIND_COUNT).fill(0);
  const kindByIndex = new Map<number, TileKind>();
  for (const tile of tiles) {
    const i = kindIndex(tile);
    counts[i] = (counts[i] ?? 0) + 1;
    if (!kindByIndex.has(i)) kindByIndex.set(i, toKind(tile));
  }
  return { counts, kindByIndex };
}

function countAt(counts: KindCounts, i: number): number {
  return counts[i] ?? 0;
}

function add(counts: KindCounts, i: number, delta: number): void {
  counts[i] = countAt(counts, i) + delta;
}

/** Kind `i` may start a run: suit tile with rank ≤ 7. Honors never form runs;
 * cross-suit runs are impossible because offsets ≤ 6 keep i+1/i+2 in-suit. */
function canStartRun(i: number): boolean {
  return i < SUIT_KIND_COUNT && i % RANKS.length <= MAX_RUN_START_OFFSET;
}

/**
 * Backtracking: build exactly `need` sets from `counts`, or return `null` if
 * impossible. Same branch order as `WinChecker.canFormMelds` — pung tried
 * before chi at every step — which is what "prefers triplets over runs"
 * means in practice.
 */
function decomposeSets(counts: KindCounts, need: number, kindByIndex: Map<number, TileKind>): ConcealedSet[] | null {
  let first = 0;
  while (first < KIND_COUNT && countAt(counts, first) === 0) first++;
  if (first === KIND_COUNT) return need === 0 ? [] : null;
  if (need === 0) return null;

  if (countAt(counts, first) >= SET_SIZE) {
    const pungKind = kindByIndex.get(first);
    if (pungKind !== undefined) {
      add(counts, first, -SET_SIZE);
      const rest = decomposeSets(counts, need - 1, kindByIndex);
      add(counts, first, SET_SIZE);
      if (rest !== null) return [{ type: 'pung', kind: pungKind }, ...rest];
    }
  }

  if (canStartRun(first) && countAt(counts, first + 1) > 0 && countAt(counts, first + 2) > 0) {
    add(counts, first, -1);
    add(counts, first + 1, -1);
    add(counts, first + 2, -1);
    const rest = decomposeSets(counts, need - 1, kindByIndex);
    add(counts, first, 1);
    add(counts, first + 1, 1);
    add(counts, first + 2, 1);
    if (rest !== null) return [{ type: 'chi' }, ...rest];
  }

  return null;
}

/**
 * Decompose a fully concealed hand (pair + `setsNeeded` sets) into pungs vs.
 * chis. Tries every kind with ≥ 2 copies as the pair (same outer loop shape
 * as `WinChecker.isStandardWinningHand`), returning the first full
 * decomposition found. Returns `null` if `concealed` can't be decomposed
 * this way — expected to only ever be called on an already-confirmed
 * winning hand, but doesn't assume that; never throws.
 */
export function decomposeConcealedHand(
  concealed: readonly Tile[],
  setsNeeded: number,
): ConcealedDecomposition | null {
  if (setsNeeded < 0 || concealed.length !== setsNeeded * SET_SIZE + PAIR_SIZE) {
    return null;
  }
  const { counts, kindByIndex } = countsByKindIndex(concealed);
  for (let i = 0; i < KIND_COUNT; i++) {
    if (countAt(counts, i) < PAIR_SIZE) continue;
    const pairKind = kindByIndex.get(i);
    if (pairKind === undefined) continue;
    add(counts, i, -PAIR_SIZE);
    const sets = decomposeSets(counts, setsNeeded, kindByIndex);
    add(counts, i, PAIR_SIZE);
    if (sets !== null) return { pair: pairKind, sets };
  }
  return null;
}
